import { WalletSmartAccountType } from "@wallet-manager/node-types/dist/src/postgres/const";
import BigNumber from "bignumber.js";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  postBatchContractSweepSubmit,
  postBatchContractSweepPreview,
} from "src/api/merchant";
import { useDatePicker } from "src/components/DatePicker";
import { useChains } from "src/components/FetchConfig";
import { useAlerting, useTranslation } from "src/hooks";
import { enumChainIdToType } from "src/utils/constant";
import {
  amountDivideDecimals,
  amountToCryptoAmount,
  displayAmount,
  findChainInfo,
  findDecimalByChainNameAndAsset,
} from "src/utils/helper";
import {
  BatchContractSweepFilterFields,
  BatchContractSweepFormContext,
  BatchContractSweepFormContextType,
  BatchContractSweepPreviewParams,
  initBatchContractSweepFilterFields,
  useBatchContractSweepFormContext,
} from "./FormContext";
import {
  BatchContractSweepPreviewContext,
  BatchContractSweepPreviewContextType,
  BatchContractSweepPreviewData,
  BatchContractSweepSubmitParams,
  SendingAddressMap,
  useBatchContractSweepPreviewContext,
} from "./PreviewContext";
import { cleanParams } from "src/features/common/helpers/cleanParams";
import { isSweepAmountSelected } from "../components/Preview/components/Table/components/helpers/selectionHelpers";

export {
  useBatchContractSweepFormContext,
  useBatchContractSweepPreviewContext,
};

export const useBatchContractSweepContext = () => {
  const formContext = useBatchContractSweepFormContext();
  const previewContext = useBatchContractSweepPreviewContext();

  return useMemo(
    () => ({ ...formContext, ...previewContext }),
    [formContext, previewContext],
  );
};

const BatchContractSweepContextProvider = ({ children }: PropsWithChildren) => {
  const { alerting } = useAlerting();
  const { selectChainByPieces } = useChains();
  const WalletSettlementTime = useDatePicker();

  const { t } = useTranslation("batchContractSweep");
  const [fields, setFields] = useState<BatchContractSweepFilterFields>(
    initBatchContractSweepFilterFields,
  );
  const [previewData, setPreviewData] =
    useState<BatchContractSweepPreviewData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [sendingAddressesMap, setSendingAddressesMap] =
    useState<SendingAddressMap>(new Map());

  useEffect(() => {
    if (!previewData) {
      return setSendingAddressesMap(new Map());
    }

    setSendingAddressesMap(
      new Map(
        previewData.addressData.map(
          ({
            wallet_address,
            decimals,
            amount,
            wallet_version,
            risk_level,
            no_risk_level_count,
            from_auto_sweep_pending_queue,
          }) => [
            wallet_address,
            {
              sweepableAmount: amountDivideDecimals(amount, decimals),
              sweepAmount: amountDivideDecimals(amount, decimals),
              walletVersion: wallet_version,
              riskLevel: risk_level,
              noRiskLevelCount: no_risk_level_count,
              isPendingQueue: from_auto_sweep_pending_queue,
            },
          ],
        ),
      ),
    );
  }, [previewData]);

  const previewParams: BatchContractSweepPreviewParams = useMemo(() => {
    const {
      chainName,
      currency: assetName,
      riskType,
      spenderAddress,
      isSmartAccount,
      threshold,
      maxNumberOfAddresses: limit,
      walletVersion,
      isFreeBandwidthOnly,
    } = fields;

    const { start: walletSettlementDateFrom, end: walletSettlementDateTo } =
      WalletSettlementTime;

    const {
      chain_id: chainId = "",
      chain_type: chainType = "",
      support_smart_account: supportSmartAccount,
    } = selectChainByPieces({
      chain_name: chainName,
    });

    const decimals = Number(
      findDecimalByChainNameAndAsset(
        String(chainType),
        String(chainId),
        assetName,
      ),
    );

    const getTronFreeBandwidthOnly = () => {
      if (enumChainIdToType.TRON !== String(chainType) || !isSmartAccount) {
        return undefined;
      }

      return isFreeBandwidthOnly;
    };

    return {
      chainId,
      chainType,
      assetName,
      riskType,
      spenderAddress,
      walletVersion,
      threshold: isNaN(decimals)
        ? undefined
        : amountToCryptoAmount(threshold, decimals),
      limit,
      decimals: isNaN(decimals) ? undefined : decimals,
      tronFreeBandwidthOnly: getTronFreeBandwidthOnly(),
      walletSettlementDateFrom,
      walletSettlementDateTo,
    };
  }, [
    fields,
    WalletSettlementTime.start,
    WalletSettlementTime.end,
    selectChainByPieces,
    findDecimalByChainNameAndAsset,
  ]);

  const onPreview = useCallback(async () => {
    setPreviewData(null);

    if (
      !previewParams.chainType ||
      !previewParams.chainId ||
      !previewParams.threshold
    ) {
      return alerting("error", t("invalid_params"));
    }

    if (BigNumber(previewParams.threshold).isLessThanOrEqualTo(0)) {
      return alerting("warning", t("invalid_threshold_message"));
    }

    if (previewParams.limit && Number(previewParams.limit) > 1000) {
      return alerting("warning", t("maxLargerThanOneThousand"));
    }

    setIsLoading(true);

    const res = await postBatchContractSweepPreview(cleanParams(previewParams));

    setIsLoading(false);

    if (!res) {
      return;
    }

    setPreviewData({ addressData: res.sweep_records, previewParams });
  }, [previewParams]);

  const submitParams: BatchContractSweepSubmitParams = useMemo(() => {
    const {
      chainType = "",
      chainId = "",
      assetName = "",
      spenderAddress = "",
      tronFreeBandwidthOnly,
      smartAccountType,
      walletVersion = "",
      riskType = "",
      decimals,
    } = previewData?.previewParams || {};

    const sweepRecords = Array.from(sendingAddressesMap.entries()).reduce(
      (
        acc: BatchContractSweepSubmitParams["sweepRecords"],
        [fromAddress, { sweepAmount }],
      ) => {
        if (sweepAmount === "0" || decimals === undefined) {
          return acc;
        }

        return [
          ...acc,
          {
            fromAddress,
            amount: amountToCryptoAmount(sweepAmount, decimals),
            decimals,
          },
        ];
      },
      [],
    );

    return {
      assetName,
      spenderAddress,
      chainType: String(chainType),
      chainId,
      sweepRecords,
      tronFreeBandwidthOnly,
      smartAccountType,
      walletVersion,
      riskType,
    };
  }, [previewData, sendingAddressesMap]);

  const addressesSummary = useMemo(() => {
    let selectedCount = 0;

    const { totalSweepAmount, totalSweepableAmount } = Array.from(
      sendingAddressesMap.values(),
    ).reduce(
      (
        acc: { totalSweepAmount: BigNumber; totalSweepableAmount: BigNumber },
        { sweepableAmount, sweepAmount, isPendingQueue },
      ) => {
        if (isSweepAmountSelected(sweepAmount, isPendingQueue)) {
          selectedCount++;
        }

        return {
          ...acc,
          totalSweepAmount: acc.totalSweepAmount.plus(sweepAmount),
          totalSweepableAmount: acc.totalSweepableAmount.plus(sweepableAmount),
        };
      },
      { totalSweepableAmount: BigNumber(0), totalSweepAmount: BigNumber(0) },
    );

    return {
      totalCount: sendingAddressesMap.size,
      selectedCount,
      totalSweepableAmount: totalSweepableAmount.toFixed(),
      totalSweepAmount: totalSweepAmount.toFixed(),
    };
  }, [sendingAddressesMap, submitParams.assetName]);

  const toDisplayAmountWithCurrency = useCallback(
    (amount: string | number) =>
      `${displayAmount(amount, 0)} ` + submitParams.assetName,
    [displayAmount, submitParams.assetName],
  );

  const onSubmit = useCallback(async () => {
    setIsLoading(true);

    const res = await postBatchContractSweepSubmit(cleanParams(submitParams));

    setIsLoading(false);

    if (!res) {
      return;
    }

    alerting("success", t("submit_success"));
  }, [submitParams]);

  const onReset = useCallback(() => {
    setFields(initBatchContractSweepFilterFields);
    setPreviewData(null);
  }, []);

  const formContextValue: BatchContractSweepFormContextType = useMemo(
    () => ({
      isLoading,
      fields,
      setFields,
      onPreview,
      previewParams,
      WalletSettlementTimePicker: WalletSettlementTime.Picker,
      translate: t,
    }),
    [
      isLoading,
      fields,
      onPreview,
      previewParams,
      WalletSettlementTime.Picker,
      t,
    ],
  );

  const previewContextValue: BatchContractSweepPreviewContextType = useMemo(
    () => ({
      previewData,
      submitParams,
      onSubmit,
      onReset,
      sendingAddressesMap,
      setSendingAddressesMap,
      toDisplayAmountWithCurrency,
      summaryDetails: {
        addressesSummary,
        spenderAddress: submitParams.spenderAddress,
        currency: submitParams.assetName,
        chainName:
          findChainInfo(submitParams.chainType, submitParams.chainId)?.name ||
          "",
      },
      translate: t,
    }),
    [
      previewData,
      onSubmit,
      onReset,
      sendingAddressesMap,
      toDisplayAmountWithCurrency,
      addressesSummary,
      submitParams,
      t,
    ],
  );

  return (
    <BatchContractSweepFormContext.Provider value={formContextValue}>
      <BatchContractSweepPreviewContext.Provider value={previewContextValue}>
        {children}
      </BatchContractSweepPreviewContext.Provider>
    </BatchContractSweepFormContext.Provider>
  );
};

export default BatchContractSweepContextProvider;
